const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
const DEFAULT_MAX_OUTPUT_BYTES = 3 * 1024 * 1024;

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);
const SUPPORTED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/avif',
  ...HEIC_MIME_TYPES
]);
const SUPPORTED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'avif', 'heic', 'heif']);

const getExtension = (filename = '') => filename.split('.').pop()?.toLowerCase() || '';

const isHeicFile = file => (
  HEIC_MIME_TYPES.has(file.type.toLowerCase()) || ['heic', 'heif'].includes(getExtension(file.name))
);

const isSupportedImage = file => (
  SUPPORTED_MIME_TYPES.has(file.type.toLowerCase()) || SUPPORTED_EXTENSIONS.has(getExtension(file.name))
);

const loadImage = blob => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(blob);
  const image = new Image();

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('El navegador no pudo leer esta imagen. Prueba con JPG, PNG o WebP.'));
  };
  image.src = objectUrl;
});

const canvasToBlob = (canvas, quality) => new Promise((resolve, reject) => {
  canvas.toBlob(
    blob => (blob ? resolve(blob) : reject(new Error('No se pudo optimizar la imagen.'))),
    'image/jpeg',
    quality
  );
});

async function convertHeicToJpeg(file) {
  try {
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });
    return Array.isArray(converted) ? converted[0] : converted;
  } catch (error) {
    throw new Error(`No se pudo convertir "${file.name}" desde HEIC/HEIF. Prueba a exportarla como JPG.`);
  }
}

export async function normalizeImageFile(file, {
  maxDimension = 1800,
  quality = 0.82,
  maxOutputBytes = DEFAULT_MAX_OUTPUT_BYTES
} = {}) {
  if (!file) {
    throw new Error('No se ha seleccionado ninguna imagen.');
  }

  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error(`La imagen "${file.name}" supera el límite de 10 MB.`);
  }

  if (!isSupportedImage(file)) {
    throw new Error(`El formato de "${file.name}" no es compatible. Usa JPG, PNG, WebP, AVIF, HEIC o HEIF.`);
  }

  const decodableBlob = isHeicFile(file) ? await convertHeicToJpeg(file) : file;
  const image = await loadImage(decodableBlob);
  const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('El navegador no pudo preparar la imagen.');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  let optimized = await canvasToBlob(canvas, quality);
  if (optimized.size > maxOutputBytes) {
    optimized = await canvasToBlob(canvas, Math.max(0.62, quality - 0.14));
  }

  if (optimized.size > maxOutputBytes) {
    throw new Error(`No se pudo reducir "${file.name}" por debajo de ${Math.ceil(maxOutputBytes / 1024 / 1024)} MB.`);
  }

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'imagen';
  return new File([optimized], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: file.lastModified || Date.now()
  });
}

export async function normalizeImageFiles(files, options) {
  const normalized = [];
  for (const file of files) {
    normalized.push(await normalizeImageFile(file, options));
  }
  return normalized;
}
